import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Eye, Share2 } from "lucide-react";
import { ACCENT, GOLD, DARK, CARD, GREEN_SUCCESS, CARD_VARIANT } from "./constants";
import { ShimmerSweep, LiveDot } from "./MarketUI";

function DocBadge({ label, ok }) {
  return (
    <span
      className="inline-flex items-center gap-1 font-head text-[10px] uppercase tracking-[0.12em] px-2 py-0.5"
      style={{
        background: ok ? "rgba(74,222,128,0.12)" : "rgba(255,255,255,0.04)",
        color: ok ? "#4ADE80" : "#555",
        border: `1px solid ${ok ? "rgba(74,222,128,0.35)" : "#2a2a2a"}`,
      }}
    >
      {ok ? "✓" : "○"} {label}
    </span>
  );
}

const AVAIL_MAP = {
  immediately: { label: "IMMEDIATE", color: "#4ADE80", bg: "rgba(74,222,128,0.12)",  border: "rgba(74,222,128,0.35)" },
  "2_weeks":   { label: "2 WKS",     color: GOLD,      bg: "rgba(245,200,66,0.1)",   border: "rgba(245,200,66,0.3)" },
  "30_days":   { label: "30 DAYS",   color: "#FB923C", bg: "rgba(251,146,60,0.1)",   border: "rgba(251,146,60,0.3)" },
  open:        { label: "OPEN",      color: "#38BDF8", bg: "rgba(56,189,248,0.1)",   border: "rgba(56,189,248,0.3)" },
};

export function DriverCard({ driver, marketUser, onBid, onViewProfile, apiBase, token }) {
  const initials = `${(driver.first_name || "?")[0]}${(driver.last_name || "?")[0]}`.toUpperCase();
  const photoUrl = driver.market_photo_path
    ? `${apiBase}/api/market/drivers/${driver.id}/photo`
    : null;
  const canBid = marketUser?.hub === "carrier" || marketUser?.hub === "admin";

  const avail = driver.availability ? AVAIL_MAP[driver.availability] : null;
  const isTransferred = driver.market_status === "transferred";
  const statusColor =
    driver.status === "approved"
      ? GREEN_SUCCESS
      : driver.status === "rejected"
      ? "#ef4444"
      : ACCENT;

  return (
    <motion.div
      variants={CARD_VARIANT}
      whileHover={{ y: -6, transition: { duration: 0.25, ease: "easeOut" } }}
      data-testid={`driver-card-${driver.id}`}
      className="relative flex flex-col overflow-hidden group driver-card-protected"
      onContextMenu={(e) => e.preventDefault()}
      style={{
        width: "240px",
        minWidth: "240px",
        background: CARD,
        border: `1px solid rgba(255,255,255,0.07)`,
        boxShadow: "0 4px 32px rgba(0,0,0,0.5)",
        animation: isTransferred ? "hm-border-glow 3s ease-in-out infinite" : undefined,
      }}
    >
      {/* Hover glow */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse at 50% 0%, rgba(255,77,0,0.14) 0%, transparent 65%)`,
          border: `1px solid rgba(255,77,0,0.3)`,
          boxShadow: `inset 0 0 30px rgba(255,77,0,0.05)`,
        }}
      />

      {/* Portrait area */}
      <div className="relative overflow-hidden" style={{ height: "200px", background: "#0E0E0E" }}>
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: `radial-gradient(ellipse at 50% 90%, rgba(255,77,0,0.18) 0%, transparent 70%)` }}
        />

        {photoUrl ? (
          <div className="w-full h-full flex items-center justify-center">
            <motion.div
              animate={{ boxShadow: ["0 0 0px rgba(255,77,0,0.3)", "0 0 24px rgba(255,77,0,0.55)", "0 0 0px rgba(255,77,0,0.3)"] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              style={{ width: 110, height: 110, borderRadius: "50%", border: `2px solid rgba(255,77,0,0.4)`, overflow: "hidden", flexShrink: 0 }}
            >
              <img
                src={photoUrl}
                alt={`${driver.first_name} ${driver.last_name}`}
                style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top" }}
              />
            </motion.div>
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <motion.div
              animate={{ boxShadow: ["0 0 0px rgba(255,77,0,0.3)", "0 0 24px rgba(255,77,0,0.55)", "0 0 0px rgba(255,77,0,0.3)"] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="font-head font-black text-5xl flex items-center justify-center"
              style={{ width: 100, height: 100, borderRadius: "50%", background: `linear-gradient(135deg, rgba(255,77,0,0.25), rgba(255,77,0,0.08))`, border: `2px solid rgba(255,77,0,0.4)`, color: ACCENT }}
            >
              {initials}
            </motion.div>
          </div>
        )}

        {/* CDL Class badge */}
        <div className="absolute top-3 left-3 font-head font-black text-[9px] uppercase tracking-[0.15em] px-2 py-1" style={{ background: ACCENT, color: "white" }}>
          {driver.cdl_class || "CDL-A"}
        </div>
        {/* State badge */}
        <div className="absolute top-3 right-3 font-head font-bold text-[9px] uppercase tracking-[0.15em] px-2 py-1" style={{ background: "rgba(0,0,0,0.7)", border: `1px solid rgba(245,200,66,0.4)`, color: GOLD }}>
          {driver.state || "—"}
        </div>

        {/* Bottom gradient */}
        <div className="absolute bottom-0 left-0 right-0 pointer-events-none" style={{ height: 70, background: `linear-gradient(to top, ${CARD}, transparent)` }} />

        {/* Bid count badge */}
        {driver.bid_count > 0 && (
          <motion.div
            animate={{ opacity: [1, 0.7, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="absolute bottom-2 left-3 font-head font-black text-[9px] uppercase px-2 py-0.5 flex items-center gap-1"
            style={{ background: GOLD, color: DARK }}
          >
            <LiveDot color={DARK} size={6} />
            {driver.bid_count} {driver.bid_count === 1 ? "BID" : "BIDS"}
          </motion.div>
        )}

        {/* Status badge */}
        <div
          className="absolute bottom-2 right-3 font-head text-[8px] uppercase tracking-[0.12em] px-1.5 py-0.5"
          style={{ background: `${statusColor}18`, color: statusColor, border: `1px solid ${statusColor}40` }}
        >
          {driver.status === "approved" ? "AVAIL" : driver.status === "rejected" ? "CLOSED" : "NEW"}
        </div>

        {/* TRANSFERRED overlay */}
        {isTransferred && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 flex flex-col items-center justify-center"
            style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(2px)" }}
          >
            <motion.div
              animate={{ rotate: [-2, -2] }}
              className="relative px-4 py-2 text-center overflow-hidden"
              style={{ background: GOLD, transform: "rotate(-2deg)" }}
            >
              <p className="font-head font-black text-xs uppercase tracking-[0.2em]" style={{ color: DARK }}>TRANSFERRED</p>
              <ShimmerSweep color="rgba(255,255,255,0.35)" duration="2.5s" delay="1s" />
            </motion.div>
            {driver.transferred_to && (
              <p className="font-head text-[9px] uppercase tracking-[0.15em] mt-2 text-white/80">→ {driver.transferred_to}</p>
            )}
          </motion.div>
        )}
      </div>

      {/* Orange accent stripe */}
      <div style={{ height: 2, background: `linear-gradient(90deg, ${ACCENT}, ${GOLD}40)` }} />

      {/* Info block */}
      <div className="px-3 py-3 flex-1 space-y-2">
        <p className="font-head font-black text-base uppercase tracking-[0.04em] text-white leading-tight">
          {driver.first_name} {driver.last_name}
        </p>
        <p className="text-xs text-white/50">
          {[driver.city, driver.state].filter(Boolean).join(", ")}
          {(driver.cdl_experience || driver.cdl_class || driver.trailer_preference) && (
            <span className="text-white/35">
              {driver.cdl_experience ? ` · ${driver.cdl_experience}` : ""}
              {driver.cdl_class ? ` · ${driver.cdl_class}` : ""}
              {driver.trailer_preference ? ` · ${driver.trailer_preference}` : ""}
            </span>
          )}
        </p>
        <div className="flex flex-wrap gap-1">
          <DocBadge label="CDL" ok={driver.has_cdl} />
          <DocBadge label="MED" ok={driver.has_medical} />
          <DocBadge label="MVR" ok={driver.has_mvr} />
          <DocBadge label="PSP" ok={driver.has_psp} />
          <DocBadge label="CLR" ok={driver.has_clearinghouse} />
        </div>
        {avail && (
          <div className="mt-1">
            <motion.span
              data-testid={`driver-avail-badge-${driver.id}`}
              animate={{ opacity: [1, 0.75, 1] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
              className="inline-flex items-center gap-1 font-head font-black text-[9px] uppercase tracking-[0.18em] px-2 py-0.5"
              style={{ background: avail.bg, color: avail.color, border: `1px solid ${avail.border}` }}
            >
              <span style={{ width: 5, height: 5, borderRadius: "50%", background: avail.color, flexShrink: 0, display: "inline-block" }} />
              {avail.label}
            </motion.span>
          </div>
        )}
      </div>

      {/* Bid button */}
      <motion.button
        data-testid={`bid-btn-${driver.id}`}
        onClick={() => onBid(driver)}
        disabled={!canBid}
        whileTap={{ scale: 0.97 }}
        className="w-full flex items-center justify-between px-4 py-3.5 font-head text-xs font-black uppercase tracking-[0.25em] transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
        style={{ background: canBid ? ACCENT : "rgba(255,77,0,0.06)", color: canBid ? "white" : "#555" }}
        onMouseEnter={(e) => { if (canBid) e.currentTarget.style.boxShadow = `0 0 20px rgba(255,77,0,0.4)`; }}
        onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}
      >
        {canBid ? "Place Bid" : marketUser ? "Carrier Access Only" : "Login to Bid"}
        <ArrowRight className="w-3.5 h-3.5" />
      </motion.button>

      {/* View profile + share row */}
      <div className="flex" style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
        {onViewProfile && (
          <button
            data-testid={`view-profile-btn-${driver.id}`}
            onClick={() => onViewProfile(driver)}
            className="flex-1 px-4 py-2 font-head text-[10px] uppercase tracking-[0.2em] text-white/40 hover:text-white/80 flex items-center justify-center gap-1.5 transition-colors"
            style={{ background: "rgba(255,255,255,0.01)" }}
          >
            <Eye className="w-3 h-3" /> View Profile
          </button>
        )}
        <button
          data-testid={`share-profile-btn-${driver.id}`}
          title="Copy shareable link"
          onClick={() => {
            // Use the server-assigned public_slug (sanitized, collision-safe).
            // Fallback to simple name-based slug only if public_slug is missing.
            const slug = driver.public_slug ||
              `${driver.first_name}-${driver.last_name}`.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
            const url = `https://hylascompanion.com/driver/${slug}`;
            navigator.clipboard.writeText(url).catch(() => {});
            import("sonner").then(({ toast }) => toast.success("Profile link copied!"));
          }}
          className="px-3 py-2 transition-colors hover:bg-white/5 text-white/25 hover:text-white/60"
          style={{ borderLeft: onViewProfile ? "1px solid rgba(255,255,255,0.05)" : "none" }}
        >
          <Share2 className="w-3 h-3" />
        </button>
      </div>
    </motion.div>
  );
}
